// apps/spa/src/components/reminders/ScheduleEditor.tsx
// REMIND-07 — segmented control (Daily / Weekly / Manual / Event) + conditional fields.
//
// Controlled component:
//   value: SchedulePreset (the discriminated union from lib/schedule.ts)
//   onChange: (preset: SchedulePreset) => void
//
// Daily preset:
//   * <input type='time' /> for hhmm
// Weekly preset:
//   * shadcn <Select> day-of-week (cron values 0-6, Sunday=0)
//   * <input type='time' /> for hhmm
// Manual preset:
//   * static caption "This reminder only fires when an officer presses Post Now."
// Event preset (Plan 04-02):
//   * BindingSelector: 'Specific event' radio (disabled when no events) + 'All future events' radio
//   * Specific event: shadcn Select populated from useEvents()
//   * LeadTimeChips: 6 buttons (15m / 30m / 1h / 2h / 12h / 24h), single-select
//
// Below: 'Next run: <relative>' line for cron presets; appropriate label for others.

import { useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import {
  nextRunRelative,
  presetToCron,
  type SchedulePreset,
} from '@/lib/schedule';
import { useEvents } from '@/hooks/useEvents';

export interface ScheduleEditorProps {
  value: SchedulePreset;
  onChange: (next: SchedulePreset) => void;
}

const DAYS: Array<{ value: 0 | 1 | 2 | 3 | 4 | 5 | 6; label: string }> = [
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
  { value: 0, label: 'Sunday' },
];

const LEAD_TIME_OPTIONS: Array<{ minutes: number; label: string }> = [
  { minutes: 15, label: '15m' },
  { minutes: 30, label: '30m' },
  { minutes: 60, label: '1h' },
  { minutes: 120, label: '2h' },
  { minutes: 720, label: '12h' },
  { minutes: 1440, label: '24h' },
];

// Link to BASTN Discord server for "create an event" guidance.
// Officers are logged in and know their server — discord.com/channels redirects appropriately.
const BASTN_DISCORD_URL = 'https://discord.com/channels/@me';

export function ScheduleEditor({ value, onChange }: ScheduleEditorProps) {
  const { data: events = [] } = useEvents();

  // Stash hhmm across preset switches so toggling between Daily/Weekly
  // doesn't lose the user's typed time. Manual/Event have no hhmm — when leaving
  // them we fall back to a sensible default.
  const currentHhmm =
    value.type === 'daily' || value.type === 'weekly' ? value.hhmm : '09:00';

  const cron = presetToCron(value);
  const relative = useMemo(
    () => (cron === null ? null : nextRunRelative(cron)),
    [cron],
  );

  function selectPreset(type: SchedulePreset['type']) {
    if (type === 'manual') {
      onChange({ type: 'manual' });
      return;
    }
    if (type === 'daily') {
      onChange({ type: 'daily', hhmm: currentHhmm });
      return;
    }
    if (type === 'event') {
      onChange({ type: 'event', eventMatchMode: 'all', leadMinutes: 30 });
      return;
    }
    // weekly — preserve hhmm, default Tuesday if no day yet
    const dayOfWeek =
      value.type === 'weekly' ? value.dayOfWeek : (2 as const);
    onChange({ type: 'weekly', dayOfWeek, hhmm: currentHhmm });
  }

  const presetTypes: Array<{ key: SchedulePreset['type']; label: string }> = [
    { key: 'daily', label: 'Daily' },
    { key: 'weekly', label: 'Weekly' },
    { key: 'manual', label: 'Manual' },
    { key: 'event', label: 'Event' },
  ];

  return (
    <div className="space-y-3" data-slot="schedule-editor">
      <div role="radiogroup" aria-label="Schedule preset" className="flex gap-2 flex-wrap">
        {presetTypes.map(({ key: presetType, label }) => {
          const isActive = value.type === presetType;
          return (
            <Button
              key={presetType}
              type="button"
              role="radio"
              aria-checked={isActive}
              variant={isActive ? 'default' : 'outline'}
              size="sm"
              onClick={() => selectPreset(presetType)}
              className={cn(isActive && 'pointer-events-none')}
            >
              {label}
            </Button>
          );
        })}
      </div>

      {value.type === 'daily' && (
        <div className="space-y-1">
          <Label htmlFor="schedule-time">Time</Label>
          <input
            id="schedule-time"
            type="time"
            value={value.hhmm}
            onChange={(e) =>
              onChange({ type: 'daily', hhmm: e.target.value })
            }
            className="border-input bg-transparent rounded-md border px-3 py-2 text-sm"
          />
        </div>
      )}

      {value.type === 'weekly' && (
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1">
            <Label htmlFor="schedule-day">Day of week</Label>
            <Select
              value={String(value.dayOfWeek)}
              onValueChange={(v) =>
                onChange({
                  type: 'weekly',
                  dayOfWeek: Number(v) as 0 | 1 | 2 | 3 | 4 | 5 | 6,
                  hhmm: value.hhmm,
                })
              }
            >
              <SelectTrigger id="schedule-day" aria-label="Day of week">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DAYS.map((d) => (
                  <SelectItem key={d.value} value={String(d.value)}>
                    {d.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="schedule-time-weekly">Time</Label>
            <input
              id="schedule-time-weekly"
              type="time"
              value={value.hhmm}
              onChange={(e) =>
                onChange({
                  type: 'weekly',
                  dayOfWeek: value.dayOfWeek,
                  hhmm: e.target.value,
                })
              }
              className="border-input bg-transparent rounded-md border px-3 py-2 text-sm"
            />
          </div>
        </div>
      )}

      {value.type === 'manual' && (
        <p className="text-muted-foreground text-sm">
          This reminder only fires when an officer presses Post Now.
        </p>
      )}

      {value.type === 'event' && (
        <div className="space-y-4">
          {/* Binding selector */}
          <div className="space-y-2" role="radiogroup" aria-label="Event binding">
            {/* Specific event option */}
            <div className="space-y-1">
              {events.length === 0 ? (
                <div>
                  <label className="flex items-center gap-2">
                    <span
                      role="radio"
                      aria-checked="false"
                      aria-disabled="true"
                      aria-label="Specific event"
                      className="inline-block h-4 w-4 rounded-full border border-current opacity-40 cursor-not-allowed"
                    />
                    <span className="text-muted-foreground text-sm opacity-60 cursor-not-allowed select-none">
                      Specific event
                    </span>
                  </label>
                  <p className="text-muted-foreground text-xs ml-6 mt-1">
                    No upcoming events scheduled in BASTN. Create one in Discord, then come back to attach this reminder.{' '}
                    <a
                      href={BASTN_DISCORD_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline"
                    >
                      Open BASTN
                    </a>
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="event-binding"
                      checked={value.eventMatchMode === 'specific'}
                      onChange={() =>
                        onChange({ type: 'event', eventMatchMode: 'specific', eventId: value.eventId, leadMinutes: value.leadMinutes })
                      }
                      className="h-4 w-4"
                    />
                    <span className="text-sm">Specific event</span>
                  </label>
                  {value.eventMatchMode === 'specific' && (
                    <div className="ml-6">
                      <Select
                        value={value.eventId ?? ''}
                        onValueChange={(id) =>
                          onChange({ type: 'event', eventMatchMode: 'specific', eventId: id || undefined, leadMinutes: value.leadMinutes })
                        }
                      >
                        <SelectTrigger aria-label="Select event">
                          <SelectValue placeholder="Select an event…" />
                        </SelectTrigger>
                        <SelectContent>
                          {events.map((e) => (
                            <SelectItem key={e.id} value={e.id}>
                              {e.name} — {new Date(e.scheduledStartTime).toLocaleDateString()}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* All future events option */}
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="event-binding"
                checked={value.eventMatchMode === 'all'}
                onChange={() =>
                  onChange({ type: 'event', eventMatchMode: 'all', leadMinutes: value.leadMinutes })
                }
                className="h-4 w-4"
              />
              <span className="text-sm">All future events</span>
            </label>
          </div>

          {/* Lead time chips */}
          <div className="space-y-1">
            <Label>Lead time before event</Label>
            <div className="flex flex-wrap gap-2" role="group" aria-label="Lead time">
              {LEAD_TIME_OPTIONS.map(({ minutes, label }) => {
                const isSelected = value.leadMinutes === minutes;
                return (
                  <Button
                    key={minutes}
                    type="button"
                    size="sm"
                    variant={isSelected ? 'default' : 'outline'}
                    aria-pressed={isSelected}
                    onClick={() =>
                      onChange({ ...value, leadMinutes: minutes })
                    }
                    className={cn(isSelected && 'pointer-events-none')}
                  >
                    {label}
                  </Button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <p className="text-muted-foreground text-xs">
        {value.type === 'manual'
          ? 'Manual reminder'
          : value.type === 'event'
            ? value.eventMatchMode === 'all'
              ? 'Fires before every BASTN event'
              : value.eventId
                ? 'Fires before the selected event'
                : 'Select an event above'
            : `Next run: ${relative ?? '—'}`}
      </p>
    </div>
  );
}
